import '../../modules/standalone/boot.js';
import { onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, setPersistence, browserSessionPersistence } from "firebase/auth";
import { doc, setDoc, serverTimestamp, Timestamp, updateDoc, onSnapshot, runTransaction } from "firebase/firestore";
import { auth, db } from '../../../config/firebase-config.js';
import { Logger } from '../../../config/logger.js';
import { SessionManager, fetchPublicIP } from '../../modules/session-manager.js';
import { PriceService } from '../../modules/price-service.js';
import { performLogout, consumeLogoutNotice, bounded, CANCEL_MS } from '../../modules/logout-service.js';
import { GlobalAudioWatcher } from '../../services/global-audio-watcher.js';
import { initAppConfig, getDefaultPage, getAuth, getBranding } from '../../modules/config-loader.js';
import { clearDocCache } from '../../modules/session-doc-cache.js';

import { initializeFooter } from '../../../pages/components/footer.js';
import { PAGES } from '../../../config/page-registry.js';

let userDocListenerUnsubscribe = null;
let currentFirm = null;

/**
 * Best-effort, BOUNDED cancel of this user's live prices, reused by every forced signOut(auth)
 * site in this file (a Firestore-connect timeout, a session-limit refusal, an admin kick, a
 * permission-denied snapshot error, post-registration) — none of which can call the canonical
 * performLogout() (see the comment on the first signOut() site below), but all of which end a
 * session and so must not leave a firm's quotes live and hittable (BUG-057's own lesson).
 *
 * `currentFirm` is only non-null once a session has actually finished loading (set at the bottom
 * of the main onSnapshot handler), so this is a genuine no-op — not a wasted write — for the sites
 * that fire before a session was ever established (session-limit refusal, post-registration).
 *
 * MUST be awaited by the caller before signOut(auth) runs: the write needs a still-valid auth
 * token, and signOut() drops it. Firing-and-forgetting this (as the kick path used to) races the
 * cancellation against the credential being revoked out from under it. Bounded via the exact same
 * bounded()/CANCEL_MS logout-service.js uses, so a wedged offline write can't hang the sign-out
 * forever either — reused, not reimplemented (AGENTS §3).
 */
async function cancelKnownFirmPrices(label) {
    if (!currentFirm) return;
    const r = await bounded(PriceService.cancelAllPrices(currentFirm), CANCEL_MS, `cancel live prices (${label})`);
    if (r.ok) {
        Logger.persistent(`[AUTH] Cancelled live prices for ${currentFirm} before ${label} (${r.value ?? 'unknown count'}).`);
    } else {
        Logger.error(`[AUTH] Could NOT confirm price cancellation for ${currentFirm} before ${label}.`);
    }
}

export async function initializeAuth() {

    // Enforce session persistence (logout on browser close)
    await setPersistence(auth, browserSessionPersistence).catch((error) => {
        Logger.error("[AUTH] Failed to set session persistence:", error);
    });

    const userStatusText = document.getElementById('user-status-text');
    const logoutButton = document.getElementById('logout-button');

    onAuthStateChanged(auth, async (user) => {
        Logger.persistent('[AUTH] onAuthStateChanged triggered');
        Logger.persistent('[AUTH] Current user:', user ? user.email : 'null');
        Logger.persistent('[AUTH] Current page:', window.location.pathname);

        // Clean up any existing listener from previous logins
        if (userDocListenerUnsubscribe) {
            userDocListenerUnsubscribe();
            userDocListenerUnsubscribe = null;
        }

        if (user) {
            Logger.persistent('[AUTH] User is authenticated, attaching snapshot listener...');
            const userDocRef = doc(db, 'admin_users', user.uid);

            // Use sessionStorage so every individual tab is treated as a unique session
            let localSessionId = sessionStorage.getItem('session_id');
            if (!localSessionId) {
                // First time this tab is authenticating, or storage was cleared
                localSessionId = crypto.randomUUID();
                sessionStorage.setItem('session_id', localSessionId);
                // Claim the session in Firestore (fire and forget, snapshot will pick it up)
                updateDoc(userDocRef, { sessionId: localSessionId }).catch(e => {
                    Logger.error("Failed to update session ID:", e);
                });
            }

            // Start session tracking (heartbeat, login metadata)
            SessionManager.trackLogin(user.uid).catch(e => Logger.error('Session track failed:', e));

            let hasProcessedInitialSnapshot = false;
            let isClaimingSession = false; // Guard: prevents false 'kicked' detection during async claim

            const isOffline = window.navigator && !window.navigator.onLine;
            const timeoutMs = isOffline ? 3000 : 15000;
            
            // Firestore connectivity timeout
            const firestoreTimeout = setTimeout(async () => {
                if (!hasProcessedInitialSnapshot) {
                    Logger.error(`[AUTH] Firestore connection timed out (${timeoutMs}ms). Backend unreachable.`);
                    const appName = getBranding().app_name;
                    alert(`Cannot connect to the ${appName} backend.\n\nPossible causes:\n• VPN/firewall blocking Google Cloud services\n• Network connectivity issue\n• AppCheck/ReCAPTCHA blocked by network\n\nPlease check your network connection and try again.`);
                    // Every sign-out must clear the per-session doc cache (BUG-057's own lesson,
                    // generalised): the next user to sign in on this tab must not read a still-TTL'd
                    // admin_users/user_settings cache entry belonging to someone else. This path
                    // (and the others below) predates logout-service.js and cannot simply call
                    // performLogout() — it fires mid-onSnapshot, before/instead of a normal session
                    // ever being claimed, and performLogout()'s own session-release/price-cancel
                    // steps assume a claimed session that these paths don't have. currentFirm is
                    // realistically null here (no snapshot has ever landed in this flow), but
                    // cancelKnownFirmPrices() is a no-op in that case — cheap insurance against the
                    // rarer case of a stale value surviving from an earlier onAuthStateChanged firing
                    // in the same tab. See its own doc comment for why this must run BEFORE signOut().
                    await cancelKnownFirmPrices('Firestore-connect timeout');
                    clearDocCache();
                    signOut(auth).then(() => {
                        window.location.href = '/pages/auth/login.html';
                    });
                }
            }, timeoutMs);

            userDocListenerUnsubscribe = onSnapshot(userDocRef, async (userDoc) => {
                clearTimeout(firestoreTimeout);

                if (!userDoc.exists()) {
                    Logger.error("[AUTH] ERROR: Authenticated user has no Firestore document:", user.uid);
                    if (userStatusText) {
                        userStatusText.textContent = `${user.email} (Error: No profile)`;
                    }
                    return;
                }

                const userData = userDoc.data();

                // --- ACTIVE SESSIONS ARRAY MANAGEMENT ---
                let activeSessions = userData.activeSessions || [];
                const maxSessions = userData.maxSessions || 1;
                const nowMs = Date.now();

                const getMillis = (ts) => {
                    if (!ts) return nowMs;
                    if (typeof ts.toMillis === 'function') return ts.toMillis();
                    if (ts.seconds) return ts.seconds * 1000;
                    return nowMs;
                };

                // 1. Prune stale sessions (no activity for > 10 minutes)
                const tenMins = 10 * 60 * 1000;
                activeSessions = activeSessions.filter(s => {
                    const lastAct = getMillis(s.lastActivity);
                    return (nowMs - lastAct) < tenMins;
                });

                // 2. Check if our session is in the array
                const mySessionIdx = activeSessions.findIndex(s => s.sessionId === localSessionId);
                const isNewSession = mySessionIdx === -1;

                if (!hasProcessedInitialSnapshot) {
                    hasProcessedInitialSnapshot = true;
                    if (isNewSession) {
                        // We are trying to log in/add this session.

                        // Fast-path refusal from the (possibly stale) snapshot — avoids opening a
                        // transaction for the common "already full" case. NOT the authoritative
                        // check: two tabs/devices logging in within the same instant would both
                        // read this same stale snapshot and both pass it. The transaction below
                        // re-reads the server's current state immediately before writing, so it's
                        // the actual enforcement point (SESSION-LIMIT-RACE fix).
                        if (activeSessions.length >= maxSessions) {
                            Logger.persistent(`[AUTH] Max sessions reached (${activeSessions.length}/${maxSessions}). Refusing login.`);
                            sessionStorage.setItem('session_kicked_reason',
                                `Maximum number of sessions already logged in. Please contact administrator if you need another session cleared.`);
                            // This particular attempt never claimed a session (refused before
                            // currentFirm is ever set below), so cancelKnownFirmPrices() is a no-op
                            // in the realistic case — kept for the same belt-and-braces reason as
                            // the Firestore-timeout site above.
                            await cancelKnownFirmPrices('session-limit refusal');
                            clearDocCache(); // see the comment on the first signOut() above
                            signOut(auth).then(() => {
                                window.location.href = '/pages/auth/login.html';
                            });
                            return;
                        }

                        // Mark that we are in the process of claiming — prevent false kick detection
                        isClaimingSession = true;

                        // Fetch real IP asynchronously, then claim the session inside a transaction.
                        // The transaction re-reads activeSessions/maxSessions at write time (not the
                        // stale onSnapshot copy captured above) so concurrent logins from two tabs/
                        // devices can no longer both pass the limit check and overwrite each other's
                        // slot — Firestore serializes/retries transactions on the same document, so
                        // whichever commits second sees the first one's session already present.
                        fetchPublicIP().then(ip => {
                            // Store IP in sessionStorage for logout tracking
                            sessionStorage.setItem('session_ip', ip);

                            runTransaction(db, async (transaction) => {
                                const freshSnap = await transaction.get(userDocRef);
                                if (!freshSnap.exists()) throw new Error('[AUTH] Profile disappeared mid-claim');
                                const freshData = freshSnap.data();

                                const nowMs2 = Date.now();
                                const tenMins2 = 10 * 60 * 1000;
                                const freshSessions = (freshData.activeSessions || []).filter(s => {
                                    const lastAct = getMillis(s.lastActivity);
                                    return (nowMs2 - lastAct) < tenMins2;
                                });
                                const freshMax = freshData.maxSessions || 1;

                                // Already claimed (e.g. a retried transaction) — nothing to do.
                                if (freshSessions.some(s => s.sessionId === localSessionId)) return;

                                if (freshSessions.length >= freshMax) {
                                    const limitErr = new Error('SESSION_LIMIT_REACHED');
                                    limitErr.code = 'SESSION_LIMIT_REACHED';
                                    throw limitErr;
                                }

                                freshSessions.push({
                                    sessionId: localSessionId,
                                    ip: ip,
                                    startedAt: new Date().toISOString(),
                                    lastActivity: Timestamp.now(),
                                    isOnline: true
                                });

                                const loginHistory = freshData.loginHistory || [];
                                loginHistory.push({
                                    sessionId: localSessionId,
                                    ip: ip,
                                    action: 'login',
                                    timestamp: new Date().toISOString(),
                                    userAgent: (navigator.userAgent || 'unknown').substring(0, 120)
                                });
                                while (loginHistory.length > 50) loginHistory.shift();

                                Logger.persistent(`[AUTH] Claiming session slot from IP ${ip}. Total: ${freshSessions.length}/${freshMax}`);
                                transaction.update(userDocRef, {
                                    activeSessions: freshSessions,
                                    loginHistory,
                                    lastLogin: serverTimestamp(),
                                    loginIP: ip,
                                    userAgent: navigator.userAgent || 'unknown',
                                    isOnline: true
                                });
                            }).then(() => {
                                isClaimingSession = false;
                                Logger.persistent('[AUTH] Session claimed successfully.');
                            }).catch(async (e) => {
                                isClaimingSession = false;
                                if (e && e.code === 'SESSION_LIMIT_REACHED') {
                                    Logger.persistent('[AUTH] Max sessions reached at claim time (lost the race). Refusing login.');
                                    sessionStorage.setItem('session_kicked_reason',
                                        `Maximum number of sessions already logged in. Please contact administrator if you need another session cleared.`);
                                    // Same no-op-in-the-realistic-case reasoning as the fast-path
                                    // refusal above — this attempt never claimed a session either.
                                    await cancelKnownFirmPrices('session-limit race loss');
                                    clearDocCache(); // see the comment on the first signOut() above
                                    signOut(auth).then(() => {
                                        window.location.href = '/pages/auth/login.html';
                                    });
                                    return;
                                }
                                Logger.error("Failed to update activeSessions:", e);
                            });
                        });
                        return; // Wait for the very next snapshot which will include our ID
                    }
                } else {
                    // Subsequent snapshots: if we are missing from the array, we were kicked (admin force-logout)!
                    // BUT: don't trigger if we're still in the process of claiming our session
                    if (isNewSession && !isClaimingSession) {
                        Logger.persistent('[AUTH] Session mismatch detected. Our session was kicked. Signing out locally.');
                        sessionStorage.setItem('session_kicked_reason', "You have been signed out because your account reached its maximum concurrent sessions.");
                        // Stop heartbeat immediately
                        SessionManager.trackLogout(user.uid).catch(() => { });

                        // Auto-cancel prices if firm is known. AWAITED — this used to be
                        // fire-and-forget (a .catch() with no await), which raced the cancel
                        // against signOut() below: if signOut() won, the write could reach
                        // Firestore with the auth token already dropped and get denied by the
                        // security rules, silently leaving the kicked firm's quotes live and
                        // hittable — the exact BUG-057 failure mode this whole mechanism exists
                        // to prevent, reintroduced by the race itself.
                        await cancelKnownFirmPrices('session kick');

                        clearDocCache(); // see the comment on the first signOut() above
                        signOut(auth).then(() => {
                            window.location.href = '/pages/auth/login.html';
                        });
                        return;
                    }
                }


                const userRole = (userData.role || 'pending');
                const isApproved = userData.approved === true;
                currentFirm = userData.firm || null;

                // Make permissions and whitelist globally accessible. Explicit permissions strictly required.
                window.currentUserPermissions = userData.permissions || {
                    canUploadMarketData: false,
                    canUploadHistData: false,
                    canInputPrices: false
                };
                window.currentUserVisibilityWhitelist = userData.visibilityWhitelist || [];
                window.currentUserReceivedPricesWhitelist = userData.receivedPricesWhitelist || [];
                window.currentUserAllowedCounterparties = userData.allowedCounterparties || null; // null = all allowed (admin not restricted)

                // Expose canonical firms list for GridRenderer Key filtering
                const brokers = userData.brokers || [];
                const mm = userData.marketmakers || [];
                const pt = userData.pricetakers || [];
                window.canonicalFirmsList = [...new Set([...brokers, ...mm, ...pt])];

                Logger.persistent('[AUTH] User role:', userRole);
                Logger.persistent('[AUTH] Approved:', isApproved);

                if (isApproved) {
                    GlobalAudioWatcher.init(currentFirm, userRole);
                }

                // Update display if elements exist
                if (userStatusText) {
                    userStatusText.textContent = `${user.email} (${userRole})`;
                }
                if (logoutButton) logoutButton.style.display = 'block';

                // --- ROLE-BASED REDIRECTION LOGIC ---
                const currentPage = window.location.pathname.split('/').pop();
                Logger.persistent('[AUTH] Checking redirect for approved user on page:', currentPage);

                // Prevent premature redirection during registration (which aborts the setDoc network request)
                if (currentPage.includes('register')) {
                    Logger.persistent('[AUTH] Skipping auto-redirect on register page to avoid interrupting write operations.');
                    return;
                }

                if (!isApproved) {
                    Logger.persistent('[AUTH] User not approved, redirecting to pending_approval.html');
                    if (!currentPage.includes('pending_approval')) {
                        window.location.href = '/pages/auth/pending_approval.html';
                    }
                } else {
                    // Config-driven page routing: default page per role from app_config/roles
                    const defaultPage = getDefaultPage(userRole);
                    const defaultPageFile = defaultPage.split('/').pop();
                    Logger.persistent(`[AUTH] Role '${userRole}' default page: ${defaultPage}`);

                    // Allow users to stay on any "allowed" app page, only redirect from auth/public pages.
                    // Derived from the page registry (2026-08-14) rather than a hand-maintained copy —
                    // this list had drifted to 8 entries against the registry's 13+ real pages (missing
                    // sabr.html, non-standard-options.html, sdr.html, yc-smoothness.html, short-end.html,
                    // mobile_trader.html; 'app.html' was dead weight — no such file exists in the repo).
                    // A page missing here means an approved user landing directly on it — a bookmark, a
                    // shared link, a refresh — gets silently bounced to their default page the next time
                    // this listener re-fires (e.g. any update to their own admin_users doc).
                    const allowedPages = PAGES.map((p) => p.href.split('/').pop());
                    const isAllowedPage = allowedPages.some(page => currentPage.includes(page));

                    if (!isAllowedPage) {
                        window.location.href = defaultPage;
                    }
                }
            }, async (error) => {
                clearTimeout(firestoreTimeout);
                Logger.error("[AUTH] Snapshot listener error:", error.message);
                if (error.code === 'permission-denied') {
                    alert("Access Denied: You do not have permission to read your user profile. This could be an AppCheck failure or a database security rule issue.");
                    // The REAL gap this file had: unlike the other forced signOut(auth) sites,
                    // this one fires on an ALREADY-ESTABLISHED listener — currentFirm can easily be
                    // set here (e.g. a role/firm reassignment mid-session revokes the read), so a
                    // user with genuinely live quotes could be signed out with no cancellation at
                    // all. Must be awaited before signOut() for the same reason as the kick path.
                    await cancelKnownFirmPrices('permission-denied sign-out');
                    clearDocCache(); // see the comment on the first signOut() above
                    signOut(auth);
                }
            });
        } else {
            // User is logged out
            Logger.persistent('[AUTH] User is logged out');
            if (userStatusText) userStatusText.textContent = "Not Logged In";
            if (logoutButton) logoutButton.style.display = 'none';

            // If logged out, they should only be on public pages (from app_config).
            const publicPages = getAuth().public_pages;
            const path = window.location.pathname;

            const isPublic = path === '/' || publicPages.some(page => path.endsWith(page) || path.endsWith(page.replace('.html', '')));

            Logger.persistent('[AUTH] Logged out user on path:', path);
            Logger.persistent('[AUTH] Is current page public?', isPublic);

            if (!isPublic) {
                Logger.persistent('[AUTH] Page is not public, redirecting to index.html');
                window.location.href = '/index.html';
            }
        }
    });

    if (logoutButton) {
        // Delegates to the canonical logout path (logout-service.js) — this used to be the
        // only COMPLETE logout implementation of four, but auth.js loads only on the
        // /pages/auth/* pages, so in practice header.js's incomplete copy ran instead for
        // every real logout (BUG-057). Keep this binding (pending_approval.html has a
        // logout button and no header nav), but never let the logic diverge again.
        logoutButton.addEventListener('click', () => performLogout());
    }

    // Show the outcome of the logout that sent us here (if any). Single-use — consumeLogoutNotice
    // clears it — so a later reload does not re-announce a stale logout.
    const noticeEl = document.getElementById('logout-notice');
    if (noticeEl) {
        const notice = consumeLogoutNotice();
        if (notice) {
            noticeEl.textContent = notice.text;
            noticeEl.classList.remove('hidden');
            noticeEl.classList.add(
                ...(notice.ok
                    ? ['bg-emerald-900/30', 'border-emerald-700', 'text-emerald-300']
                    : ['bg-red-900/30', 'border-red-700', 'text-red-300', 'font-semibold']),
            );
        }
    }

    const loginButton = document.getElementById('login-button');
    if (loginButton) {
        // Starts disabled in the static HTML — bootstrap() awaits initAppConfig() (a
        // Firestore round-trip) before reaching this point, so a click landing between
        // page load and here would otherwise be silently ignored (no listener attached
        // yet). A real user rarely notices on a fast connection, but a slow one — or any
        // scripted client — can click a button that looks ready but does nothing at all,
        // with zero feedback (found live 2026-07-31 via the E2E suite: Identity Toolkit
        // never even saw a request). Enabling it only here guarantees the listener below
        // is live before the button can be clicked.
        loginButton.disabled = false;
        loginButton.classList.remove('opacity-50', 'cursor-not-allowed');

        const kickedReason = sessionStorage.getItem('session_kicked_reason');
        if (kickedReason) {
            sessionStorage.removeItem('session_kicked_reason');
            // Show reason visually on the login page
            const msgDiv = document.createElement('div');
            msgDiv.className = 'bg-red-900/50 border border-red-500 text-red-200 text-sm rounded-md p-3 mb-4 text-center';
            msgDiv.textContent = kickedReason;
            loginButton.parentElement.insertBefore(msgDiv, loginButton);
        }

        loginButton.addEventListener('click', async () => {
            const email = document.getElementById('login-email').value;
            const password = document.getElementById('login-password').value;
            if (!email || !password) {
                alert('Please enter your email and password.');
                return;
            }
            // Show loading state
            loginButton.disabled = true;
            const originalText = loginButton.textContent;
            loginButton.textContent = 'Signing in...';
            loginButton.classList.add('opacity-50', 'cursor-not-allowed');

            // (A localStorage.removeItem('session_id') sat here until 2026-08-20 — the wrong
            // store: session ids live in sessionStorage, and logout-service.js clears that one.)
            try {
                await signInWithEmailAndPassword(auth, email, password);
                // onAuthStateChanged will handle the redirect
            } catch (error) {
                if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                    alert('Login failed: Invalid email or password.');
                } else if (error.code === 'auth/network-request-failed') {
                    alert('Login failed: Network connection error. Please check your internet connection and try again.');
                } else if (error.code === 'auth/too-many-requests') {
                    alert('Login failed: Too many failed attempts. Please wait a few minutes before trying again.');
                } else {
                    alert(`Login failed: ${error.message}`);
                }
            } finally {
                // Restore button state
                loginButton.disabled = false;
                loginButton.textContent = originalText;
                loginButton.classList.remove('opacity-50', 'cursor-not-allowed');
            }
        });
    }

    const registerForm = document.getElementById('register-form');
    if (registerForm) {
        registerForm.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = document.getElementById('register-email').value;
            const password = document.getElementById('register-password').value;
            const firstName = document.getElementById('first-name').value;
            const lastName = document.getElementById('last-name').value;
            const termsAgreed = document.getElementById('terms-agreement').checked;
            const messageDiv = document.getElementById('registration-message');
            messageDiv.textContent = ''; // Clear previous messages

            if (!firstName || !lastName || !email || !password) {
                messageDiv.textContent = "Please fill out all fields.";
                messageDiv.style.color = 'red';
                return;
            }
            if (!termsAgreed) {
                messageDiv.textContent = "You must agree to the Terms & Conditions to register.";
                messageDiv.style.color = 'red';
                return;
            }
            if (password.length < 6) {
                messageDiv.textContent = "Password must be at least 6 characters long.";
                messageDiv.style.color = 'red';
                return;
            }

            try {
                messageDiv.textContent = 'Registering...';
                messageDiv.style.color = 'gray';

                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                Logger.log('User registered in Auth:', user.uid);

                // Create a corresponding user document in Firestore
                await setDoc(doc(db, "admin_users", user.uid), {
                    firstName: firstName,
                    lastName: lastName,
                    email: email,
                    role: 'pending', // Default role for new users
                    approved: false, // Default approval status
                    firm: 'unassigned', // Default firm
                    createdAt: serverTimestamp()
                });
                Logger.log('User document created in Firestore with pending approval.');

                // Sign the user out immediately after registration. currentFirm is realistically
                // null on this page (a freshly-registered user has none, and an already-signed-in
                // privileged user reaching the public register form is its own separate, unrelated
                // edge case) — cancelKnownFirmPrices() is a no-op here in the normal case, kept for
                // the same uniform-invariant reasoning as the other forced signOut(auth) sites.
                await cancelKnownFirmPrices('post-registration sign-out');
                clearDocCache(); // see the comment on the first signOut() above
                await signOut(auth);
                Logger.log('User signed out after registration.');

                // Redirect to pending approval page
                window.location.href = '/pages/auth/pending_approval.html';

            } catch (error) {
                Logger.error("Registration failed:", error);
                let friendlyMessage = `Registration failed: ${error.message}`;
                if (error.code === 'auth/email-already-in-use') {
                    friendlyMessage = 'This email is already registered. Please log in or use a different email.';
                } else if (error.code === 'permission-denied') {
                    friendlyMessage = 'Registration failed: Database permission denied. This is a configuration issue.';
                }
                messageDiv.textContent = friendlyMessage;
                messageDiv.style.color = 'red';
            }
        });
    }
    // --- Password Toggle Logic (Centralized) ---
    const passwordInput = document.getElementById('login-password');
    const toggleBtn = document.getElementById('toggle-password-visibility');
    const eyeIcon = document.getElementById('eye-icon');
    if (toggleBtn && passwordInput) {
        toggleBtn.addEventListener('click', function () {
            const isPassword = passwordInput.type === 'password';
            passwordInput.type = isPassword ? 'text' : 'password';
            if (eyeIcon) {
                eyeIcon.innerHTML = isPassword
                    ? '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-3.134-10-7a9.978 9.978 0 013.422-5.746M6.634 6.634A9.956 9.956 0 0112 5c5.523 0 10 3.134 10 7 0 1.61-.635 3.122-1.75 4.418M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3l18 18" />'
                    : '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 3.866-4.477 7-10 7S2 15.866 2 12 6.477 5 12 5s10 3.134 10 7z" />';
            }
        });
    }

}

// --- Bootstrap Logic ---
(async function bootstrap() {
    try {
        await initAppConfig(); // Load app config before anything else
        await initializeAuth();
        await initializeFooter();
    } catch (e) {
        console.error("[AUTH] Bootstrap Error:", e);
    }
})();
